import client from './client';
import { ModifiedDutyEmployee, CreateModifiedDutyEmployeeRequest, UpdateModifiedDutyEmployeeRequest, ModifiedDutySaveResponse } from '../interfaces/ModifiedDutyInterface';

/**
 * Fetches all modified duty employees from the API
 */
export const getModifiedDutyEmployees = async (): Promise<ModifiedDutyEmployee[]> => {
    try {
        const response = await client.get("/admin/modifiedDutyEmployees");
        console.log("Modified Duty Employees:", response.data);
        return response.data;
    } catch (error) {
        console.error("Error fetching modified duty employees:", error);
        throw new Error("Failed to fetch modified duty employees");
    }
};

/**
 * Creates a new modified duty employee
 * @param newModifiedDutyEmployee - The new modified duty employee data
 * @returns The newly created modified duty employee
 */
export const createModifiedDutyEmployee = async (
    newModifiedDutyEmployee: CreateModifiedDutyEmployeeRequest
): Promise<ModifiedDutySaveResponse> => {
    try {
        const response = await client.post("/admin/modifiedDutyEmployees", newModifiedDutyEmployee);
        console.log("Modified duty employee created successfully:", response.data);
        return response.data;
    } catch (error) {
        console.error("Error creating modified duty employee:", error);
        throw new Error("Failed to create modified duty employee");
    }
};

/**
 * Updates an existing modified duty employee
 * @param id - The ID of the modified duty employee to update
 * @param updatedModifiedDutyEmployee - The updated modified duty employee data
 * @returns The updated modified duty employee
 */
export const updateModifiedDutyEmployee = async (
    id: number,
    updatedModifiedDutyEmployee: UpdateModifiedDutyEmployeeRequest
): Promise<ModifiedDutySaveResponse> => {
    try {
        const response = await client.put(`/admin/modifiedDutyEmployees/${id}`, updatedModifiedDutyEmployee);
        console.log("Modified duty employee updated successfully:", response.data);
        return response.data;
    } catch (error) {
        console.error("Error updating modified duty employee:", error);
        throw new Error("Failed to update modified duty employee");
    }
};


export type ModifiedDutyExportScope = 'Active' | 'Inactive' | 'All';

/** filename*=UTF-8''… or filename="…" from a Content-Disposition header (mirrors ProjectsAPI). Shared with sickHours.ts. */
export const getDownloadFilename = (contentDisposition: string | undefined, fallback: string): string => {
    if (!contentDisposition) return fallback;
    const star = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (star?.[1]) {
        try {
            return decodeURIComponent(star[1].trim().replace(/^"|"$/g, ""));
        } catch {
            /* fall through to the plain filename */
        }
    }
    const plain = contentDisposition.match(/filename="?([^";]+)"?/i);
    if (plain?.[1]) return plain[1].trim();
    return fallback;
};

/** Best-effort message from an axios error whose body was requested as a Blob. Shared with sickHours.ts. */
export const blobErrorMessage = async (
    error: any,
    fallback = "Failed to export modified duty records",
): Promise<string> => {
    const data = error?.response?.data;
    if (data instanceof Blob) {
        try {
            const text = await data.text();
            try {
                const json = JSON.parse(text);
                return json?.message || json?.errorMessage || json?.error || text;
            } catch {
                if (text) return text;
            }
        } catch {
            /* ignore */
        }
    }
    return error?.message || fallback;
};

/**
 * Downloads the Modified Duty .xlsx for exactly the rows on screen.
 * `ids` must be in display (sorted) order; `today` is the viewer's local date (YYYY-MM-DD).
 */
export const exportModifiedDutyEmployees = async (body: {
    ids: number[];
    scope: ModifiedDutyExportScope;
    today: string;
}): Promise<{ blob: Blob; fileName: string }> => {
    try {
        const response = await client.post("/admin/modifiedDutyEmployees/export", body, {
            responseType: "blob",
        });
        const fallbackName = `ModifiedDuty_${body.scope}_${body.today}.xlsx`;
        const contentDisposition = response.headers?.["content-disposition"] as string | undefined;
        const fileName = getDownloadFilename(contentDisposition, fallbackName);
        return { blob: response.data as Blob, fileName };
    } catch (error: any) {
        console.error("Error exporting modified duty employees:", error);
        throw new Error(await blobErrorMessage(error));
    }
};
